import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../components/layout/PageHeader';
import Table from '../../components/ui/Table';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import { CandidateCell, ReviewedCell, fmtDate, selectColumn } from '../../components/requests/RequestActions';
import Spinner from '../../components/ui/Spinner';
import EmptyState from '../../components/ui/EmptyState';
import RequestFilters, { StatusBadge, useRequestFilters } from '../../components/requests/RequestFilters';
import { PendingEditBanner } from '../../components/candidates/StepEditRequest';
import { listPendingStepEditRequests, approveStepEdit, rejectStepEdit } from '../../api/candidates';
import { usePermission } from '../../auth/usePermission';
import { BulkActionBar, useRowSelection } from '../../components/requests/BulkSelection';


const QUERY_KEY = ['pending-step-edit-requests'];

const searchFields = (row) => [
  row.candidate.full_name,
  row.candidate.reference_no,
  row.candidate.contact_no,
  row.candidate.nationality,
  row.step.title,
  row.step.pending_edit_request.requested_by,
];
const requestedAt = (row) => row.step.pending_edit_request.requested_at;

// Reviewed (approved/rejected) requests are history — no approve/reject controls, just who
// requested it and how it was resolved.
function ReviewedSummary({ request }) {
  return (
    <div className="rounded-md bg-gray-50 p-3 text-xs text-gray-600">
      <p>
        Requested by <span className="font-medium">{request.requested_by}</span> on {new Date(request.requested_at).toLocaleString()}
      </p>
      {request.request_note && <p className="mt-1 italic">"{request.request_note}"</p>}
      <p className="mt-1">
        {request.status === 'approved' ? 'Approved' : 'Rejected'} by <span className="font-medium">{request.reviewed_by}</span>
        {request.reviewed_at && <> on {new Date(request.reviewed_at).toLocaleString()}</>}
        {request.review_remarks && <span className="italic"> — "{request.review_remarks}"</span>}
      </p>
    </div>
  );
}

function ReviewButton({ row, onResolved }) {
  const [open, setOpen] = useState(false);
  const request = row.step.pending_edit_request;
  return (
    <>
      <Button variant="secondary" onClick={() => setOpen(true)}>
        Review
      </Button>
      <Modal open={open} onClose={() => setOpen(false)} title={`${row.candidate.full_name} — ${row.step.title}`}>
        {request.status === 'pending' ? (
          <PendingEditBanner
            candidate={row.candidate}
            step={row.step}
            onResolved={() => {
              setOpen(false);
              onResolved();
            }}
          />
        ) : (
          <ReviewedSummary request={request} />
        )}
      </Modal>
    </>
  );
}

const columnsFor = (selection, refetch) => [
  selectColumn(selection, (r) => r.id, (r) => r.step.pending_edit_request.status === 'pending'),
  { key: 'candidate', label: 'Candidate', render: (row) => <CandidateCell candidate={row.candidate} /> },
  { key: 'step', label: 'Step', render: (row) => row.step.title },
  {
    key: 'requested',
    label: 'Requested',
    render: (row) => (
      <div>
        {row.step.pending_edit_request.requested_by}
        <div className="text-xs text-gray-400">{fmtDate(row.step.pending_edit_request.requested_at)}</div>
        {row.step.pending_edit_request.request_note && (
          <div className="text-xs italic text-gray-500">"{row.step.pending_edit_request.request_note}"</div>
        )}
      </div>
    ),
  },
  {
    key: 'status',
    label: 'Status',
    render: (row) => (
      <div>
        <StatusBadge status={row.step.pending_edit_request.status} />
        <ReviewedCell row={row.step.pending_edit_request} />
      </div>
    ),
  },
  { key: 'actions', label: '', render: (row) => <div className="flex justify-end"><ReviewButton row={row} onResolved={refetch} /></div> },
];

export default function PendingStepEdits() {
  const queryClient = useQueryClient();
  const { filters, set, apply, reset, dirty, candidate } = useRequestFilters(searchFields, requestedAt);
  const query = useQuery({
    queryKey: [...QUERY_KEY, filters.status, candidate.params],
    queryFn: () => listPendingStepEditRequests({ ...candidate.params, status: filters.status }),
  });
  const allRows = query.data || [];
  const rows = apply(allRows);
  const { can } = usePermission();
  const selection = useRowSelection(rows, (r) => r.step.pending_edit_request.id, (r) => r.step.pending_edit_request.status === 'pending');
  const byRequestId = new Map(rows.map((r) => [r.step.pending_edit_request.id, r]));

  const refetch = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });

  return (
    <div>
      <PageHeader
        title="Edit Approvals"
        description="Completed steps (and saved field sections) staff have requested a change to — nothing takes effect until approved here."
      />

      <RequestFilters candidate={candidate} filters={filters} set={set} reset={reset} dirty={dirty} total={allRows.length} shown={rows.length} />

      {can('approve edit candidate completed step') && (
        <BulkActionBar
          selection={selection}
          onApprove={(id) => {
            const r = byRequestId.get(id);
            return approveStepEdit(r.candidate.id, r.step.candidate_step_id, id);
          }}
          onReject={(id, reason) => {
            const r = byRequestId.get(id);
            return rejectStepEdit(r.candidate.id, r.step.candidate_step_id, id, reason);
          }}
          onDone={refetch}
        />
      )}

      {query.isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title={dirty ? 'No matching requests' : 'Nothing pending'}
          message={dirty ? 'No requests match the current filters.' : 'No edit requests are currently awaiting approval.'}
        />
      ) : (
        <Table columns={columnsFor(selection, refetch)} data={rows.map((r) => ({ ...r, id: r.step.pending_edit_request.id }))} />
      )}
    </div>
  );
}
